import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import StaffLink from '../../components/shared/StaffLink';
import {
  PlusIcon, XMarkIcon, EyeIcon, CheckCircleIcon, DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function ObservationsPage() {
  const { user, isManagement, isDirector } = useAuth();
  const [observations, setObservations] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [rubric, setRubric] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);

  const { register, handleSubmit, reset } = useForm();
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    try {
      const [obsRes, staffRes, rubricRes] = await Promise.all([
        api.get('/observations'),
        api.get('/staff'),
        api.get('/observations/rubric'),
      ]);
      setObservations(obsRes.data);
      setStaffList(staffRes.data);
      setRubric(rubricRes.data);
    } catch { toast.error('Failed to load observations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForm = () => {
    setShowForm(true);
    setScores({});
    setNotes({});
    reset();
  };

  const onSubmit = async (form) => {
    try {
      const item_scores = rubric.map((r) => ({
        rubric_item_id: r.id,
        score: scores[r.id] ? Number(scores[r.id]) : null,
        notes: notes[r.id] || null,
      })).filter((s) => s.score || s.notes);

      await api.post('/observations', {
        ...form,
        observed_user_id: Number(form.observed_user_id),
        item_scores,
      });
      toast.success('Observation created');
      setShowForm(false); reset(); load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create');
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/observations/${id}`);
      setDetail(data);
    } catch { toast.error('Failed to load detail'); }
  };

  const sign = async (id) => {
    try {
      await api.post(`/observations/${id}/sign`);
      toast.success('Signed');
      if (detail?.id === id) openDetail(id);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const verify = async (id) => {
    try {
      await api.patch(`/observations/${id}/verify`);
      toast.success('Verified');
      load();
    } catch { toast.error('Failed to verify'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Classroom Observations</h1>
          <p className="text-gray-500 text-sm">{observations.length} total · BBC 12-criterion rubric</p>
        </div>
        {isManagement && (
          <button onClick={openForm} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 font-medium">
            <PlusIcon className="w-5 h-5" /> New Observation
          </button>
        )}
      </div>

      {observations.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No observations yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase">
                <th className="px-6 py-3">Teacher</th>
                <th className="px-6 py-3">Classroom</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Observer</th>
                <th className="px-6 py-3">Overall</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {observations.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(o.id)}>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">
                    <StaffLink id={o.observed_user_id} name={o.staff_name} />
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">{o.classroom_name || '—'}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{formatDate(o.observation_date)}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{o.observer_name}</td>
                  <td className="px-6 py-3">
                    {o.overall_score ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                        {Number(o.overall_score).toFixed(1)} / 5
                      </span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-6 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-6 py-3 text-right">
                    <EyeIcon className="w-4 h-4 text-gray-400 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Observation modal */}
      {showForm && (
        <Modal title="New Classroom Observation" onClose={() => setShowForm(false)} maxWidth="max-w-3xl">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teacher *</label>
                <select {...register('observed_user_id', { required: true })}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
                  <option value="">Select…</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Classroom</label>
                <input {...register('classroom_name')}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" min={DATE_FLOOR} {...register('observation_date', { required: true })}
                  defaultValue={new Date().toISOString().slice(0, 10)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observer</label>
                <input value={`${user.first_name} ${user.last_name}`} disabled
                  className="w-full rounded-xl border border-gray-100 px-3 py-2 text-sm bg-gray-50 text-gray-500" />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Rubric — 1 (unacceptable) to 5 (exceptional)</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {rubric.map((r) => (
                  <div key={r.id} className="border border-gray-100 rounded-xl p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{r.display_order}. {r.label}</p>
                        {r.prompt && <p className="text-xs text-gray-500 mt-0.5">{r.prompt}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {[1, 2, 3, 4, 5].map(n => (
                          <button key={n} type="button"
                            onClick={() => setScores({ ...scores, [r.id]: scores[r.id] === n ? null : n })}
                            className={`w-8 h-8 rounded-full text-xs font-semibold ${
                              scores[r.id] === n ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}>
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input value={notes[r.id] || ''} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      placeholder="Notes / follow-up…"
                      className="w-full mt-2 rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-orange-500 focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Observations / Notes</label>
              <textarea {...register('additional_notes')} rows={3}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Strengths</label>
                <textarea {...register('strengths')} rows={2}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Areas for Improvement</label>
                <textarea {...register('areas_for_improvement')} rows={2}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Items</label>
                <textarea {...register('action_items')} rows={2}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:border-orange-500 focus:outline-none resize-none" />
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">
                Submit Observation
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Detail */}
      {detail && (
        <Modal title="Observation Detail" onClose={() => setDetail(null)} maxWidth="max-w-3xl">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><strong>Teacher:</strong> <StaffLink id={detail.observed_user_id} name={detail.staff_name} /></div>
              <div><strong>Classroom:</strong> {detail.classroom_name || '—'}</div>
              <div><strong>Date:</strong> {formatDate(detail.observation_date)}</div>
              <div><strong>Observer:</strong> {detail.observer_name}</div>
              <div><strong>Status:</strong> <StatusBadge status={detail.status} /></div>
              {detail.overall_score && <div><strong>Overall:</strong> {Number(detail.overall_score).toFixed(1)} / 5</div>}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Rubric Scores</h3>
              <div className="space-y-2">
                {detail.item_scores?.length > 0 ? detail.item_scores.map(s => (
                  <div key={s.id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{s.display_order}. {s.label}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        s.score >= 4 ? 'bg-green-100 text-green-700' :
                        s.score >= 3 ? 'bg-amber-100 text-amber-700' :
                        s.score ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {s.score ? `${s.score} / 5` : '—'}
                      </span>
                    </div>
                    {s.notes && <p className="text-xs text-gray-600 mt-1">{s.notes}</p>}
                  </div>
                )) : <p className="text-sm text-gray-400">No rubric scores recorded</p>}
              </div>
            </div>

            {(detail.strengths || detail.areas_for_improvement || detail.action_items || detail.additional_notes) && (
              <div className="grid grid-cols-2 gap-3">
                {detail.strengths && <Field label="Strengths" value={detail.strengths} />}
                {detail.areas_for_improvement && <Field label="Areas for Improvement" value={detail.areas_for_improvement} />}
                {detail.action_items && <Field label="Action Items" value={detail.action_items} />}
                {detail.additional_notes && <Field label="Additional Notes" value={detail.additional_notes} />}
              </div>
            )}

            {detail.signatures?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Signatures</h3>
                <div className="space-y-1">
                  {detail.signatures.map(s => (
                    <div key={s.id} className="text-xs text-gray-600 flex items-center gap-2">
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      {s.first_name} {s.last_name} ({s.role}) — {new Date(s.signed_at).toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-3 border-t border-gray-100">
              {detail.observed_user_id === user.id && detail.status === 'submitted' && (
                <button onClick={() => sign(detail.id)} className="bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-600">
                  Sign as Teacher
                </button>
              )}
              {isDirector && detail.status === 'acknowledged' && (
                <button onClick={() => verify(detail.id)} className="bg-green-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-600">
                  Verify (Director)
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function Modal({ title, onClose, maxWidth = 'max-w-md', children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
